import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {
    CreateScoringModelRequest,
    ScoringActionResponse,
    ScoringModelResponse,
    TrainModelRequest
} from "../../model/dto/api/propensity-scoring.interface";

/**
 * Service for managing Propensity Scoring Models (F-029).
 * Provides operations for creating, training, and managing scoring models.
 */
@Injectable({
    providedIn: 'root'
})
export class PropensityScoringModelService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/propensityScoringModels')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-brain';
    }

    /**
     * Creates a new propensity scoring model.
     * F-029: Configure ML models for propensity scoring.
     *
     * @param request the model creation request
     */
    createModel(request: CreateScoringModelRequest): Observable<ScoringActionResponse> {
        return this.postGeneric<ScoringActionResponse>(request, '/create-model');
    }

    /**
     * Activates a propensity scoring model.
     *
     * @param modelId the model ID
     */
    activateModel(modelId: number): Observable<ScoringActionResponse> {
        return this.postGeneric<ScoringActionResponse>({}, `/${modelId}/activate`);
    }

    /**
     * Deactivates a propensity scoring model.
     *
     * @param modelId the model ID
     */
    deactivateModel(modelId: number): Observable<ScoringActionResponse> {
        return this.postGeneric<ScoringActionResponse>({}, `/${modelId}/deactivate`);
    }

    /**
     * Trains a model using historical campaign data.
     * F-029: Given historical data exists, when model is trained, then accuracy improves over time.
     *
     * @param modelId the model ID
     * @param campaignIds list of campaign IDs for training
     */
    trainModel(modelId: number, campaignIds: number[]): Observable<ScoringActionResponse> {
        const request: TrainModelRequest = {campaignIds};
        return this.postGeneric<ScoringActionResponse>(request, `/${modelId}/train`);
    }

    /**
     * Gets all active scoring models.
     */
    getActiveModels(): Observable<ScoringModelResponse[]> {
        return this.getGeneric<ScoringModelResponse[]>('/active');
    }

    /**
     * Gets model details by ID.
     *
     * @param modelId the model ID
     */
    getModelDetails(modelId: number): Observable<ScoringModelResponse> {
        return this.getGeneric<ScoringModelResponse>(`/${modelId}/details`);
    }
}
